import type { ReactNode } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { sendAccountPasswordResetEmail } from 'hooks/public-account-requests';

import { getRedirectPathForUser } from 'containers/admin/users/user/user-profile/user-profile.helpers';

import Notification from 'components/partials/notification/notification.helper';

import { LOCK_TEXT } from 'constants/texts';

import { renderModal } from 'helpers/modal-render';

import Icons from 'assets/icons';

import type { UseImpersonationResult } from 'hooks/use-impersonation';
import type { UseCurrentUserResult } from 'state/current-user/current-user.thunk';
import toast from 'src/components/partials/toast/toast';

interface UserLike {
  id: string | number;
  email: string;
  status?: string;
  fdUser?: boolean;
  viewPortalAsUser?: boolean;
  firstName?: string;
  lastName?: string;
  name?: string;
  [key: string]: unknown;
}

interface ClientLike {
  id?: string | number;
  status?: string;
  [key: string]: unknown;
}

interface CurrentUserDataPermissions {
  sendPasswordReset?: boolean;
  lockAndUnlockAllUsers?: boolean;
  lockAndUnlockClientUsers?: boolean;
  addAndDeleteUsers?: boolean;
}

interface CurrentUserShape {
  data?: CurrentUserDataPermissions;
}

export interface UserActionMenuItem {
  label: string;
  icon: ReactNode;
  isDelete?: boolean;
  onClick: () => void;
}

interface GetUserActionMenuItemsArgs {
  user: UserLike;
  currentClient?: ClientLike | null;
  reloadUser: () => void;
  currentUser: UseCurrentUserResult | CurrentUserShape;
  impersonation: UseImpersonationResult;
  navigate: NavigateFunction;
  lockUser?: (user: UserLike) => Promise<unknown>;
  deleteUser?: (id: string | number) => Promise<unknown>;
}

export const getUserActionMenuItems = ({
  user,
  currentClient,
  reloadUser,
  currentUser,
  impersonation,
  navigate,
  lockUser,
  deleteUser,
}: GetUserActionMenuItemsArgs): UserActionMenuItem[] => {
  const userProfileActionMenuData: Array<
    UserActionMenuItem | false | undefined
  > = [
    Boolean(currentUser?.data?.sendPasswordReset) && {
      label: 'Send reset password link',
      icon: <Icons.Link customClass="mr-2 text-gray-600" />,
      onClick: () => {
        sendAccountPasswordResetEmail(user.email)
          .then(() => {
            Notification.flash(
              'Sent password reset email',
              'success',
              'Check your email for a password reset link'
            );
          })
          .catch(
            (error: { errorSummary?: string; data?: { detail?: string } }) => {
              Notification.flash(
                'Error sending password reset email',
                'success',
                (error && error.errorSummary) ||
                  (error.data && error.data.detail) ||
                  ''
              );
            }
          );
      },
    },
    !!currentClient &&
      currentClient.status !== 'Expired' &&
      user?.status !== 'LOCKED' &&
      user?.viewPortalAsUser && {
        label: 'View portal as user',
        icon: <Icons.Eye customClass="mr-2" />,
        onClick: () =>
          impersonation.impersonate(user as never, window.location.pathname),
      },
    Boolean(
      currentUser?.data?.lockAndUnlockAllUsers ||
        (currentUser?.data?.lockAndUnlockClientUsers && !user?.fdUser)
    ) && {
      label: `${user?.status === 'LOCKED' ? 'Unlock' : 'Lock'} account`,
      icon: <Icons.Lock customClass="mr-2" />,
      onClick: () => {
        const isStatusLocked = user?.status === 'LOCKED';

        const lockedText = isStatusLocked ? 'unlock' : 'lock';

        const headerText = `Are you sure you want to ${lockedText} this user account?`;

        const text = isStatusLocked
          ? LOCK_TEXT.CONFIRM_UNLOCK_USER
          : LOCK_TEXT.CONFIRM_LOCK_USER;

        const notificationMode = isStatusLocked ? 'success' : 'warning';

        renderModal({
          headerText,
          text,
          onConfirmCallback: () =>
            lockUser?.(user).then(() => {
              reloadUser();
              toast[notificationMode]({
                title: `User ${lockedText}ed`,
                message: `User ${user?.firstName} ${user?.lastName} was successfully ${lockedText}ed`,
              });
            }),
          onConfirmButtonLabel: `Yes, ${lockedText} account`,
        });
      },
    },
    Boolean(currentUser?.data?.addAndDeleteUsers) && {
      label: 'Delete user',
      icon: <Icons.Delete customClass="text-critical-500 mr-2" />,
      isDelete: true,
      onClick: () =>
        renderModal({
          headerText: `Are you sure you want to delete this user account?`,
          onConfirmCallback: () => {
            const redirectPath = getRedirectPathForUser({
              user,
              currentClient,
            });

            deleteUser?.(user.id).then(() => {
              toast.info({
                title: 'User deleted',
                message: `You've successfully deleted ${user.name} user`,
              });
              navigate(redirectPath);
            });
          },
          onConfirmButtonLabel: 'Yes, delete account',
        }),
    },
  ];

  return userProfileActionMenuData.filter((item): item is UserActionMenuItem =>
    Boolean(item)
  );
};
